import { Middleware } from 'redux'
import { isActionOf } from 'typesafe-actions'
import { createComparisonPdf, comparisonPdfCreationFailed } from '../../actions/app/comparisonPdf.actions'
import { createNotification } from '../../actions/app/notification.actions'
import { addPendingRequest, removePendingRequest } from '../../actions/core/pendingRequests.actions'
import getTranslatedComparisonPdfPayload from '../../selectors/comparison/helpers/getTranslatedComparisonPdfPayload'
import getApiOptions from '../../../getApiOptions'
import brandSelector from '../../selectors/brandSelector'
import languageSelector from '../../selectors/i18n/languageSelector'
import countrySelector from '../../selectors/i18n/countrySelector'
import removeNullFields from '../../selectors/equipments/helpers/removeNullFields'

const comparisonPdfMiddleware = (
    networkRequest: typeof fetch,
    console: Console,
    pdfServiceUrl: string,
): Middleware => (store) => (next) => (action) => { /* eslint-disable-line consistent-return */
    next(action)
    const { dispatch, getState } = store

    if (isActionOf(createComparisonPdf, action)) {
        const payload = action.payload
        const state = getState()
        const brand = brandSelector(state).toUpperCase()
        const language = languageSelector(state)
        const countryCode = countrySelector(state).toUpperCase()

        const payloadWithMetadata: typeof payload = {
            ...payload,
            metadata: payload.metadata || {
                brand,
                language,
                countryCode,
                creationDate: new Date().toISOString(),
                info: {
                    pageHeadline: 'comparisonResultPageHeadline',
                    standardEquipment: 'standard',
                    optionalEquipment: 'optional',
                    unknownEquipment: 'notAvailable',
                    carlineHeader: 'carlineNameHeader',
                    vehicleCodeHeader: `vehicleCodeHeader_${brand.toLowerCase()}`,
                },
            },
        }

        const sanitizedPayload = removeNullFields(payloadWithMetadata)
        const translatedPayload = getTranslatedComparisonPdfPayload(sanitizedPayload)

        const handleError = (type: string) => (error: Error) => {
            dispatch(comparisonPdfCreationFailed({
                failureAction: comparisonPdfCreationFailed,
                error,
                type: type as any,
                url: pdfServiceUrl,
            }, {
                causedBy: action,
            }))
        }

        const handleResponse = (response: Response): void => {
            if (!response.ok) {
                const error = new Error(response.statusText)
                handleError('response')(error)
                return
            }

            response.blob()
                .then((resBlob) => {
                    const pdfBlob = new Blob([resBlob], { type: 'application/pdf' })
                    const objUrl = URL.createObjectURL(pdfBlob)
                    const link = document.createElement('a')

                    link.href = objUrl
                    link.download = `Comparison-${brand}-${countryCode}.pdf`
                    link.click()

                    setTimeout(() => { window.URL.revokeObjectURL(objUrl) }, 500)

                    dispatch(removePendingRequest(pdfServiceUrl, { causedBy: createComparisonPdf(payload) }))
                })
                .catch((e) => {
                    console.error(e)

                    const error = new Error(response.statusText)

                    handleError('response')(error)
                })
        }

        dispatch(addPendingRequest(pdfServiceUrl, { causedBy: createComparisonPdf(payload) }))

        const options: RequestInit = getApiOptions({
            method: 'POST',
            body: JSON.stringify(translatedPayload),
            addSessionIdHeader: false,
        })

        return networkRequest(encodeURI(`${pdfServiceUrl}/comparison`), options)
            .then(handleResponse)
            .catch(handleError('fetch'))
    }

    if (isActionOf(comparisonPdfCreationFailed, action)) {
        dispatch(removePendingRequest(pdfServiceUrl, { causedBy: createComparisonPdf(action.payload as any) }))

        console.error(action.payload)

        dispatch(createNotification({
            type: 'error',
            message: 'error.pdf',
        }))
    }
}

export default comparisonPdfMiddleware
